import React, { useState, useEffect, useRef, useCallback } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import "./Nav.css";

// --- Universal slugify (Armenian + Russian + English) ---
function slugify(text) {
  const translitMap = {
    Ա: "a", Բ: "b", Գ: "g", Դ: "d", Ե: "e", Զ: "z", Է: "e", Ը: "y", Թ: "t",
    Ժ: "zh", Ի: "i", Լ: "l", Խ: "kh", Ծ: "ts", Կ: "k", Հ: "h", Ձ: "dz", Ղ: "gh",
    Ճ: "ch", Մ: "m", Յ: "y", Ն: "n", Շ: "sh", Ո: "o", Չ: "ch", Պ: "p", Ջ: "j",
    Ռ: "r", Ս: "s", Վ: "v", Տ: "t", Ր: "r", Ց: "ts", Ւ: "v", Փ: "p", Ք: "q",
    Օ: "o", Ֆ: "f",
    ա: "a", բ: "b", գ: "g", դ: "d", ե: "e", զ: "z", է: "e", ը: "y", թ: "t",
    ժ: "zh", ի: "i", լ: "l", խ: "kh", ծ: "ts", կ: "k", հ: "h", ձ: "dz", ղ: "gh",
    ճ: "ch", մ: "m", յ: "y", ն: "n", շ: "sh", ո: "o", չ: "ch", պ: "p", ջ: "j",
    ռ: "r", ս: "s", վ: "v", տ: "t", ր: "r", ց: "ts", ւ: "v", փ: "p", ք: "q",
    օ: "o", ֆ: "f",
    А: "a", Б: "b", В: "v", Г: "g", Д: "d", Е: "e", Ё: "yo", Ж: "zh", З: "z",
    И: "i", Й: "y", К: "k", Л: "l", М: "m", Н: "n", О: "o", П: "p", Р: "r",
    С: "s", Т: "t", У: "u", Ф: "f", Х: "kh", Ц: "ts", Ч: "ch", Ш: "sh", Щ: "sch",
    Ъ: "", Ы: "y", Ь: "", Э: "e", Ю: "yu", Я: "ya",
    а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "yo", ж: "zh", з: "z",
    и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r",
    с: "s", т: "t", у: "u", ф: "f", х: "kh", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
    ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
  };

  return text
    .toString()
    .split("")
    .map((ch) => translitMap[ch] || ch)
    .join("")
    .normalize("NFKD")
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/--+/g, "-")
    .toLowerCase()
    .trim();
}

export default function Nav({ gridView, setGridView, activeCategory, setActiveCategory }) {
  const [categories, setCategories] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // --- Fetch categories ---
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const q = query(collection(db, "menu"), orderBy("order", "asc"));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().category,
          iconUrl: doc.data().iconUrl,
        }));
        setCategories(data);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  // --- Center Active Button in Nav ---
  const centerButton = useCallback((categoryName) => {
    const nav = navRef.current;
    const btn = document.getElementById(`btn-${slugify(categoryName)}`);
    if (nav && btn) {
      const navRect = nav.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const offset = btnRect.left - navRect.left - navRect.width / 2 + btnRect.width / 2;
      nav.scrollBy({ left: offset, behavior: "smooth" });
    }
  }, []);

  // --- Sync Nav Scroll with Active Category ---
  useEffect(() => {
    if (activeCategory && categories.length > 0) {
      // Օգտագործում ենք requestAnimationFrame՝ ապահովելու համար, որ DOM-ը թարմացված է
      requestAnimationFrame(() => centerButton(activeCategory));
    }
  }, [activeCategory, categories, centerButton]);

  // --- Երբ activeCategory-ն փոխվում է, էջը ավտոմատ բարձրանում է վերև ---
  useEffect(() => {
    window.scrollTo(0, 0);
}, [activeCategory]);

  const handleClick = (name) => {
    if (setActiveCategory) {
      setActiveCategory(name);
    }
  };

  // --- Drag scroll for nav bar ---
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const startDrag = (x) => {
      isDown.current = true;
      startX.current = x - nav.offsetLeft;
      scrollLeft.current = nav.scrollLeft;
    };
    const moveDrag = (x, e) => {
      if (!isDown.current) return;
      e.preventDefault();
      const walk = (x - startX.current) * 1.3;
      nav.scrollLeft = scrollLeft.current - walk;
    };
    const endDrag = () => (isDown.current = false);

    const onMouseDown = (e) => startDrag(e.pageX);
    const onMouseMove = (e) => moveDrag(e.pageX, e);
    const onTouchStart = (e) => startDrag(e.touches[0].pageX);
    const onTouchMove = (e) => moveDrag(e.touches[0].pageX, e);

    nav.addEventListener("mousedown", onMouseDown);
    nav.addEventListener("mousemove", onMouseMove);
    nav.addEventListener("mouseup", endDrag);
    nav.addEventListener("mouseleave", endDrag);
    nav.addEventListener("touchstart", onTouchStart, { passive: true });
    nav.addEventListener("touchmove", onTouchMove, { passive: false });
    nav.addEventListener("touchend", endDrag, { passive: true });

    return () => {
      nav.removeEventListener("mousedown", onMouseDown);
      nav.removeEventListener("mousemove", onMouseMove);
      nav.removeEventListener("mouseup", endDrag);
      nav.removeEventListener("mouseleave", endDrag);
      nav.removeEventListener("touchstart", onTouchStart);
      nav.removeEventListener("touchmove", onTouchMove);
      nav.removeEventListener("touchend", endDrag);
    };
  }, []);

  return (
    <>
      {/* --- Top Bar --- */}
      <div className="qr-nav-topbar">
        <button
          className={`menu-btn ${menuOpen ? "open" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle Menu"
        >
          <span></span><span></span><span></span>
        </button>

        <div className="qr-nav-logo">
          <img src="/logo.png" alt="Logo" />
        </div>

        {/* View Toggle Button */}
        <button
          className="view-toggle-btn"
          onClick={() => setGridView(!gridView)}
          aria-label="Toggle view mode"
        >
          {gridView ? (
            <img src="/icons/single-view.png" alt="Single View" />
          ) : (
            <img src="/icons/double-view.png" alt="Double View" />
          )}
        </button>
      </div>

      {/* --- Sidebar --- */}
      <div className={`sidebar ${menuOpen ? "open" : ""}`}>
        <button className="close-btn" onClick={() => setMenuOpen(false)}>✕</button>
        <ul>
          <li><a href="/">Գլխավոր</a></li>
          <li><a href="/about">Մեր մասին</a></li>
          <li><a href="/contact">Կապ</a></li>
        </ul>
        <div className="sidebar-social">
          <a href="https://www.facebook.com/tjvjiktavern/?locale=hy_AM" target="_blank" rel="noopener noreferrer">
            <img src="/icons/facebook.png" alt="Facebook" />
          </a>
          <a href="https://www.instagram.com/tjvjik_tavern/" target="_blank" rel="noopener noreferrer">
            <img src="/icons/instagram.png" alt="Instagram" />
          </a>
        </div>
      </div>

      {/* --- Scrollable Categories --- */}
      <nav className="qr-nav" ref={navRef}>
        <div className="qr-nav-buttons">
          {categories.map(({ id, name, iconUrl }) => {
            const slug = slugify(name);
            const isActive = activeCategory === name;
            
            return (
              <button
                key={id}
                id={`btn-${slug}`}
                onClick={() => handleClick(name)}
                className={`qr-nav-button ${isActive ? "active" : ""}`}
              >
                <span className="qr-nav-text">{name}</span>
                {iconUrl && <img src={iconUrl} alt="" className="section-icon-iconUrl" />}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}